// La banda superior de una pantalla: va a SANGRE COMPLETA contra el menú, sin aire arriba ni a los
// lados.
//
// ── Por qué existe ───────────────────────────────────────────────────────────────────────────────
// El `AppShell` le pone `px-5 py-4` a toda pantalla, porque casi todas lo quieren. Pero en los
// mockups la primera franja —la barra de KPIs del Home, la de rango de Análisis, la de filtros de
// Excepciones— va PEGADA al topbar: sin ese corte visual, la franja se lee como una card más y deja
// de funcionar como cabecera.
//
// Conseguirlo pide cancelar el padding del shell con márgenes negativos. Eso se hizo primero a mano
// en Home (`-mx-5 -mt-4`) y el resultado fue el esperable: cada pantalla nueva lo re-descubría, y
// la que se olvidaba quedaba con la franja flotando. Peor: si el shell cambia su padding, todas las
// pantallas que lo cancelan a mano se rompen a la vez y en silencio.
//
// Acá el negativo sale de `SHELL_CONTENT_BLEED`, que es el par exacto de `SHELL_CONTENT_PADDING`.
// Cambiar el aire del shell ya no obliga a recorrer las pantallas.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { SHELL_CONTENT_BLEED } from '../AppShell';

export function ScreenBand({
    children,
    className,
    /**
     * `head` es la franja pegada al topbar (el caso normal). `foot` la deja al fondo con el borde
     * arriba — se usa cuando la banda resume lo que está encima, no lo que viene debajo.
     */
    edge = 'head',
}: {
    children: ReactNode;
    className?: string;
    edge?: 'head' | 'foot';
}) {
    return (
        <div
            className={cn(
                // `flex-none`: la banda nunca se encoge cuando el contenido de abajo crece.
                'flex flex-none items-center gap-2 overflow-x-auto bg-card px-3 py-2',
                SHELL_CONTENT_BLEED,
                edge === 'head' ? 'border-b border-border' : 'border-t border-border',
                className,
            )}
        >
            {children}
        </div>
    );
}

/**
 * El cuerpo que va DEBAJO de una `<ScreenBand>`.
 *
 * También sangra a los lados: si la banda va a sangre completa y el cuerpo no, las dos quedan
 * desalineadas y se nota. El aire lateral lo repone el propio cuerpo, que así controla su densidad
 * —un grid de cards quiere menos que un formulario— sin pelearse con el shell.
 */
export function ScreenBody({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <div className={cn('-mx-5 min-h-0 flex-1 overflow-y-auto bg-background p-2.5', className)}>
            {children}
        </div>
    );
}
